import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Select,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconCrown, IconPlus, IconTrash } from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import {
  useProjectMembers,
  useRemoveMember,
  useTransferOwnership,
  useUpdateMemberRole,
  type ProjectMember,
} from '../../queries/project-members';
import { AddMemberDrawer } from './AddMemberDrawer';
import { useAuthStore } from '../../auth/auth-store';
import { useProjectStore } from '../../project/project-store';
import type { ProjectRole } from '../../types/api';
import { ApiError } from '../../lib/api-error';

interface Props {
  projectId: string;
}

const EDITABLE_ROLES: { value: ProjectRole; label: string }[] = [
  { value: 'moderator', label: 'moderator' },
  { value: 'editor', label: 'editor' },
  { value: 'viewer', label: 'viewer' },
];

export function MembersTab({ projectId }: Props) {
  const { data: members = [], isLoading } = useProjectMembers(projectId);
  const me = useAuthStore((s) => s.user);
  const myRole = useProjectStore((s) => s.projectRole);
  const canManage = myRole === 'owner' || myRole === 'moderator';
  const [addOpen, setAddOpen] = useState(false);

  const updateRoleM = useUpdateMemberRole(projectId);
  const removeM = useRemoveMember(projectId);
  const transferM = useTransferOwnership(projectId);
  const isOwner = myRole === 'owner';

  const existingIds = new Set(members.map((m) => m.user_id));

  async function changeRole(m: ProjectMember, role: ProjectRole) {
    try {
      await updateRoleM.mutateAsync({ userId: m.user_id, role });
      notifications.show({
        title: 'Role updated',
        message: `${m.username} -> ${role}`,
        color: 'pine',
      });
    } catch (err) {
      notifications.show({
        title: 'Update failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  function confirmTransfer(m: ProjectMember) {
    modals.openConfirmModal({
      title: `Transfer ownership to ${m.username}?`,
      children: (
        <Stack gap="xs">
          <Text size="sm">
            <b>{m.username}</b> becomes the new owner. You are demoted to{' '}
            <b>moderator</b>: you keep most write permissions, but you can no
            longer add new members, transfer ownership, or delete the project.
          </Text>
          <Text size="sm" c="dimmed">
            This is irreversible from your account once you click Transfer.
          </Text>
        </Stack>
      ),
      labels: { confirm: 'Transfer ownership', cancel: 'Cancel' },
      confirmProps: { color: 'grape' },
      onConfirm: async () => {
        try {
          await transferM.mutateAsync({ new_owner_id: m.user_id });
          notifications.show({
            title: 'Ownership transferred',
            message: `${m.username} is now the owner`,
            color: 'pine',
          });
        } catch (err) {
          notifications.show({
            title: 'Transfer failed',
            message: err instanceof ApiError ? err.message : 'Unexpected error',
            color: 'red',
          });
        }
      },
    });
  }

  function confirmRemove(m: ProjectMember) {
    modals.openConfirmModal({
      title: `Remove ${m.username}?`,
      children: (
        <Text size="sm">Removes them from this project. Membership history is audited.</Text>
      ),
      labels: { confirm: 'Remove', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        try {
          await removeM.mutateAsync(m.user_id);
          notifications.show({ title: 'Member removed', message: m.username, color: 'pine' });
        } catch (err) {
          notifications.show({
            title: 'Remove failed',
            message: err instanceof ApiError ? err.message : 'Unexpected error',
            color: 'red',
          });
        }
      },
    });
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {members.length} member{members.length === 1 ? '' : 's'}
        </Text>
        {canManage && (
          <Button size="sm" leftSection={<IconPlus size={14} />} onClick={() => setAddOpen(true)}>
            Add member
          </Button>
        )}
      </Group>

      <Paper withBorder radius="md">
        <DataTable<ProjectMember>
          records={members}
          fetching={isLoading}
          minHeight={160}
          noRecordsText="No members"
          columns={[
            { accessor: 'username', title: 'Username' },
            { accessor: 'email', title: 'Email' },
            {
              accessor: 'role',
              title: 'Role',
              width: 180,
              render: (m) => {
                const isOwner = m.role === 'owner';
                const isSelf = m.user_id === me?.id;
                if (isOwner || !canManage || isSelf) {
                  return (
                    <Badge color={roleColor(m.role)} variant="light">
                      {m.role}
                    </Badge>
                  );
                }
                // Moderators cannot touch the owner; already handled above
                return (
                  <Select
                    size="xs"
                    data={EDITABLE_ROLES}
                    value={m.role === 'owner' ? 'moderator' : m.role}
                    onChange={(v) => v && changeRole(m, v as ProjectRole)}
                  />
                );
              },
            },
            {
              accessor: 'joined_at',
              title: 'Joined',
              width: 140,
              render: (m) => dayjs(m.joined_at).format('YYYY-MM-DD'),
            },
            {
              accessor: 'actions',
              title: '',
              width: 90,
              render: (m) => {
                if (m.role === 'owner' || !canManage) return null;
                const isSelf = m.user_id === me?.id;
                if (isSelf && myRole === 'moderator') return null; // moderator can leave via a separate flow
                return (
                  <Group gap={4} justify="flex-end">
                    {isOwner && !isSelf && (
                      <Tooltip label="Transfer ownership to this member">
                        <ActionIcon
                          size="sm"
                          variant="subtle"
                          color="grape"
                          onClick={() => confirmTransfer(m)}
                        >
                          <IconCrown size={14} />
                        </ActionIcon>
                      </Tooltip>
                    )}
                    <Tooltip label="Remove">
                      <ActionIcon
                        size="sm"
                        variant="subtle"
                        color="red"
                        onClick={() => confirmRemove(m)}
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                );
              },
            },
          ]}
        />
      </Paper>

      <AddMemberDrawer
        opened={addOpen}
        onClose={() => setAddOpen(false)}
        projectId={projectId}
        existingMemberIds={existingIds}
      />
    </Stack>
  );
}

function roleColor(r: ProjectRole): string {
  switch (r) {
    case 'owner':
      return 'grape';
    case 'moderator':
      return 'blue';
    case 'editor':
      return 'teal';
    case 'viewer':
    default:
      return 'gray';
  }
}
